// Comments and suggestions drawn in the text (the Mac's `highlightAnchors`): the app resolves each open anchor to where
// its passage is now (fragment, UTF-16 range) and hands them over with `setAnnotations`; they are drawn as marks —
// comments washed (one in a category underlined in the category's colour), agents' a different wash, suggestions struck
// through with the proposed text after them — and
// mapped through every edit until the app sends them again. Nothing here is stored: annotations are view state.
//
// `annotationAt` tells the app which annotation the caret is in (the margin follows it); `annotationRange` gives an
// annotation's range as it stands now, for selecting it.
import { StateEffect, StateField, type EditorState, type Extension, type Range } from '@codemirror/state';
import { Decoration, EditorView, WidgetType, type DecorationSet } from '@codemirror/view';
import { layoutField, setLayout } from './state.ts';

export interface Annotation {
  id: string;
  fragmentId: string;
  /** UTF-16 offsets within the fragment's text. A suggestion may be empty (an insertion). */
  from: number;
  to: number;
  kind: 'comment' | 'agent' | 'suggestion';
  /** A suggestion's proposed text, drawn after the passage it replaces. */
  proposed?: string;
  /** A comment's category colour (an ink's name): the passage is underlined in it, over its usual wash. */
  ink?: string;
}

interface Placed {
  a: Annotation;
  from: number;
  to: number;
}

interface AnnotationState {
  placed: Placed[];
  active: string | null;
  deco: DecorationSet;
}

/** Replaces the annotations shown. */
export const setAnnotations = StateEffect.define<Annotation[]>();
/** Marks one annotation as the one the margin has chosen (null: none). */
export const setActiveAnnotation = StateEffect.define<string | null>();

class ProposedWidget extends WidgetType {
  constructor(readonly text: string, readonly id: string, readonly active: boolean) { super(); }
  eq(o: ProposedWidget) { return o.text === this.text && o.id === this.id && o.active === this.active; }
  toDOM() {
    const el = document.createElement('span');
    el.className = 'ws-suggestion-proposed' + (this.active ? ' ws-annotation-active' : '');
    el.setAttribute('data-annotation', this.id);
    el.textContent = this.text || '⌫';
    if (!this.text) el.setAttribute('aria-label', 'cut');
    return el;
  }
  ignoreEvent() { return false; }
}

function decorations(placed: Placed[], active: string | null): DecorationSet {
  const ranges: Range<Decoration>[] = [];
  for (const { a, from, to } of placed) {
    const on = a.id === active ? ' ws-annotation-active' : '';
    const category = a.kind !== 'suggestion' && a.ink && /^[a-z]+$/.test(a.ink) ? ` ws-category ws-category-${a.ink}` : '';
    const cls = (a.kind === 'suggestion' ? 'ws-suggestion' : a.kind === 'agent' ? 'ws-comment ws-comment-agent' : 'ws-comment') + category;
    if (to > from) ranges.push(Decoration.mark({ class: cls + on, attributes: { 'data-annotation': a.id } }).range(from, to));
    if (a.kind === 'suggestion') ranges.push(Decoration.widget({ widget: new ProposedWidget(a.proposed ?? '', a.id, !!on), side: 1 }).range(to));
  }
  return Decoration.set(ranges, true);
}

/** Fragment offsets to document positions, clamped to the fragment's body; unknown fragments are left out. */
function place(state: EditorState, list: Annotation[]): Placed[] {
  const layout = state.field(layoutField);
  const out: Placed[] = [];
  for (const a of list) {
    const i = layout.index(a.fragmentId);
    if (i === undefined) continue;
    const length = layout.to[i] - layout.from[i];
    const from = layout.from[i] + Math.max(0, Math.min(a.from, length));
    const to = layout.from[i] + Math.max(0, Math.min(a.to, length));
    if (to < from || (to === from && a.kind !== 'suggestion')) continue;
    out.push({ a, from, to });
  }
  return out.sort((x, y) => x.from - y.from || x.to - y.to);
}

export const annotationField = StateField.define<AnnotationState>({
  create: () => ({ placed: [], active: null, deco: Decoration.none }),
  update(value, tr) {
    let { placed, active } = value;
    let changed = false;
    for (const e of tr.effects) {
      if (e.is(setAnnotations)) {
        placed = place(tr.state, e.value);
        changed = true;
      } else if (e.is(setActiveAnnotation)) {
        active = e.value;
        changed = true;
      } else if (e.is(setLayout)) {
        placed = []; // another selection: the app sends the new one's
        changed = true;
      }
    }
    if (tr.docChanged && !changed) {
      // Mapped as Automerge cursors move: each end stays with the character after it.
      placed = placed
        .map((p) => ({ a: p.a, from: tr.changes.mapPos(p.from, 1), to: tr.changes.mapPos(p.to, 1) }))
        .filter((p) => p.to > p.from || p.a.kind === 'suggestion');
      return { placed, active, deco: decorations(placed, active) };
    }
    return changed ? { placed, active, deco: decorations(placed, active) } : value;
  },
  provide: (f) => EditorView.decorations.from(f, (v) => v.deco),
});

/** The annotations the position is in (innermost last), for the margin to follow the caret. */
export function annotationsAt(state: EditorState, pos: number): string[] {
  return state.field(annotationField, false)?.placed.filter((p) => p.from <= pos && pos <= p.to && (p.to > p.from || p.from === pos)).map((p) => p.a.id) ?? [];
}

/** An annotation's range as it stands now, in its fragment's UTF-16 offsets. */
export function annotationRange(state: EditorState, id: string): { fragmentId: string; from: number; to: number } | null {
  const p = state.field(annotationField, false)?.placed.find((x) => x.a.id === id);
  if (!p) return null;
  const layout = state.field(layoutField);
  const i = layout.index(p.a.fragmentId);
  if (i === undefined) return null;
  return { fragmentId: p.a.fragmentId, from: p.from - layout.from[i], to: p.to - layout.from[i] };
}

/** The extension, calling `onCaret` with the annotations the caret is in whenever it moves. */
export function annotations(onCaret?: (ids: string[]) => void): Extension {
  return [
    annotationField,
    onCaret
      ? EditorView.updateListener.of((u) => {
          if (u.selectionSet) onCaret(annotationsAt(u.state, u.state.selection.main.head));
        })
      : [],
  ];
}
